import React, { type ReactNode, useCallback, useMemo } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import Link from '@atlassian/jira-common-analytics-v2-wrapped-components/src/link';
import PageHeader from '@atlassian/jira-common-components-page-header';
import { FormattedI18nMessage } from '@atlassian/jira-formatted-i18n-message/src/ui/index.tsx';
import { DocumentTitle } from '@atlassian/jira-global-document-title';
import HelpPanelLink from '@atlassian/jira-help-panel-button';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import {
	FALLBACK_SUPPORT_ARTICLE,
	LEARN_MORE_IPH_ARTICLE_ID,
} from '@atlassian/jira-issue-navigator-changeboarding/src/common/constants.tsx';
import {
	ContextualAnalyticsData,
	SCREEN,
	FireScreenAnalytics,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import SubmitApdex from '@atlassian/jira-providers-spa-apdex-analytics/src/submit-apdex-mark/index.tsx';
import { PageLayout } from '@atlassian/jira-spa-apps-page-layout/src/page/index.tsx';
import { useLockInTransition } from '../services/lock-in-transition/index.tsx';
import { newIssueSearchTransitionPageLoad } from '../utils/performance-analytics';
import LockInTransition from './lock-in-transition';
import messages from './messages';

export const NewIssueSearchTransitionPage = () => {
	const onClickLearnMore = useCallback((_: unknown, analytics: UIAnalyticsEvent) => {
		fireUIAnalytics(analytics, 'learnMore');
	}, []);

	const { formatMessage } = useIntl();

	const learnMoreComponent = useMemo(
		() => ({
			link: (props: { children: ReactNode }) => (
				<HelpPanelLink
					fallbackComponent={
						<LinkStyled
							href={FALLBACK_SUPPORT_ARTICLE}
							target="_blank"
							rel="noopener noreferrer"
							onClick={onClickLearnMore}
						>
							{props.children}
						</LinkStyled>
					}
					articleId={LEARN_MORE_IPH_ARTICLE_ID}
					onClick={onClickLearnMore}
					appearance="link"
					spacing="none"
				>
					{props.children}
				</HelpPanelLink>
			),
		}),
		[onClickLearnMore],
	);

	const [{ loading: lockedInToggleLoading, value: isLockedIn }, { updateLockInToggle }] =
		useLockInTransition();

	return (
		<ContextualAnalyticsData sourceName="newIssueSearchTransition" sourceType={SCREEN}>
			<DocumentTitle title={formatMessage(messages.title)} />

			<PageLayout>
				<PageHeader>{formatMessage(messages.title)}</PageHeader>

				<SectionParagraph>
					<FormattedMessage {...messages.summary} />
				</SectionParagraph>
				<SectionParagraph>
					<FormattedI18nMessage
						componentsMapping={learnMoreComponent}
						message={formatMessage(messages.learnMore, {
							linkStart: '{linkStart}',
							linkEnd: '{linkEnd}',
						})}
					/>
				</SectionParagraph>
				<SectionParagraph>
					<FormattedMessage {...messages.toggleHeaderEnabled} />
				</SectionParagraph>

				<SectionWrapper>
					<LockInTransition
						loading={lockedInToggleLoading}
						setLockInToggle={updateLockInToggle}
						value={isLockedIn}
					/>
				</SectionWrapper>
			</PageLayout>

			<FireScreenAnalytics />

			<SubmitApdex
				appName="jira.new-issue-search-transition"
				// @ts-expect-error - TS2769 - No overload matches this call.
				metric={newIssueSearchTransitionPageLoad}
			/>
		</ContextualAnalyticsData>
	);
};

export default NewIssueSearchTransitionPage;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SectionParagraph = styled.p({
	marginTop: token('space.250', '20px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SectionWrapper = styled.div({
	display: 'flex',
	marginTop: token('space.300', '24px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LinkStyled = styled(Link)({
	textDecoration: 'underline',
});
